import { useMemo, useState } from 'react';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useFormContext } from 'react-hook-form';

import { SingleSelect, Text } from '@myrealtrip/web-ui';
import type { Option } from '@myrealtrip/web-ui';

import type { PointToPointTicketFormProps } from '~types/pointToPointTicket';

const CURRENT_YEAR = new Date().getFullYear();

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function calculateAge(birthDate: Date, departureDate: Date): number {
  const age = departureDate.getFullYear() - birthDate.getFullYear();
  const birthMonthDay = birthDate.getMonth() * 100 + birthDate.getDate();
  const departureMonthDay = departureDate.getMonth() * 100 + departureDate.getDate();

  return departureMonthDay >= birthMonthDay ? age : age - 1;
}

const yearOptions: Option[] = Array.from({ length: CURRENT_YEAR - 1900 + 1 }, (_, i) => {
  const y = String(CURRENT_YEAR - i);
  return { label: y, value: y };
});

const monthOptions: Option[] = Array.from({ length: 12 }, (_, i) => {
  const m = String(i + 1);
  return { label: m, value: m };
});

const TravelerAgeCalculator = () => {
  const theme = useTheme();
  const { watch } = useFormContext<PointToPointTicketFormProps>();
  const departureDate = watch('departureDate');

  const [year, setYear] = useState<string | null>(null);
  const [month, setMonth] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(null);

  const hasDepartureDate = !!departureDate;

  const dayOptions: Option[] = useMemo(() => {
    const maxDay = year && month ? getDaysInMonth(Number(year), Number(month)) : 31;
    return Array.from({ length: maxDay }, (_, i) => {
      const d = String(i + 1);
      return { label: d, value: d };
    });
  }, [year, month]);

  const handleYearChange = (value: string | null) => {
    setYear(value);
    if (day && value && month) {
      const maxDay = getDaysInMonth(Number(value), Number(month));
      if (Number(day) > maxDay) setDay(null);
    }
  };

  const handleMonthChange = (value: string | null) => {
    setMonth(value);
    if (day && year && value) {
      const maxDay = getDaysInMonth(Number(year), Number(value));
      if (Number(day) > maxDay) setDay(null);
    }
  };

  const birthDate =
    year && month && day ? new Date(Number(year), Number(month) - 1, Number(day)) : null;

  const calculatedAge =
    birthDate && hasDepartureDate ? calculateAge(birthDate, new Date(departureDate)) : null;

  return (
    <Container>
      <Text typography="paragraphBold14" color={theme.colors.gray.s1000}>
        만 나이 확인해보기
      </Text>

      <FieldGroup>
        <FieldRow>
          <FieldLabel>
            <Text typography="paragraphNormal13" color={theme.colors.gray.s700}>
              탑승일
            </Text>
          </FieldLabel>
          {hasDepartureDate ? (
            <DateDisplay>
              <Text typography="paragraphNormal13" color={theme.colors.gray.s1000}>
                {departureDate}
              </Text>
            </DateDisplay>
          ) : (
            <DateDisplay>
              <Text typography="paragraphNormal13" color={theme.colors.gray.s1000}>
                탑승일을 입력해주세요
              </Text>
            </DateDisplay>
          )}
        </FieldRow>

        <FieldRow>
          <FieldLabel>
            <Text typography="paragraphNormal13" color={theme.colors.gray.s700}>
              생년월일
            </Text>
          </FieldLabel>
          <SelectGroup>
            <SelectWrapper>
              <SingleSelect
                options={yearOptions}
                value={year}
                onChange={handleYearChange}
                placeholder="년"
                size="medium"
                append
              />
            </SelectWrapper>
            <SelectWrapper>
              <SingleSelect
                options={monthOptions}
                value={month}
                onChange={handleMonthChange}
                placeholder="월"
                size="medium"
                append
              />
            </SelectWrapper>
            <SelectWrapper>
              <SingleSelect
                options={dayOptions}
                value={day}
                onChange={setDay}
                placeholder="일"
                size="medium"
                append
              />
            </SelectWrapper>
          </SelectGroup>
        </FieldRow>
      </FieldGroup>

      {calculatedAge !== null && (
        <ResultBox>
          <Text typography="paragraphNormal13" color={theme.colors.gray.s700}>
            {year}년 {month}월 {day}일 출생 → {departureDate} 탑승 기준
          </Text>
          <ResultAge>
            <Text typography="headlineBold18" color={theme.colors.blue.s500}>
              만 {calculatedAge}세
            </Text>
          </ResultAge>
        </ResultBox>
      )}
    </Container>
  );
};

const Container = styled.div`
  margin: 12px 20px 0;
  padding: 16px;
  background: ${({ theme: { palettes } }) => palettes.blue[50]};
  border-radius: 12px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldLabel = styled.div`
  flex-shrink: 0;
  width: 56px;
`;

const DateDisplay = styled.div`
  flex: 1;
  border-radius: 8px;
`;

const SelectGroup = styled.div`
  flex: 1;
  display: flex;
  gap: 4px;
`;

const SelectWrapper = styled.div`
  flex: 1;
  min-width: 0;

  button {
    font-size: 13px;
  }

  button > div {
    min-width: 70px;
    min-height: 35px;
  }

  ul {
    max-height: 200px;
  }

  li {
    font-size: 13px;
    padding: 6px 8px;
  }
`;

const ResultBox = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid ${({ theme: { colors } }) => colors.gray.s200};
  text-align: center;
`;

const ResultAge = styled.div`
  margin-top: 4px;
`;

export default TravelerAgeCalculator;
